import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save, FileEdit } from 'lucide-react';

import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { useAuth } from '../contexts/AuthContext';
import { getCcnlDettaglio, getCcnlDisponibili } from '../services/sindacatoApi';
import { creaVersioneCcnl } from '../services/operatoreApi';

const oggi = () => new Date().toISOString().slice(0, 10);

function nuovoLivello() {
  return {
    livello: '', pagaBaseMensile: '', scattoAnzianitaImporto: '', mansioni: '',
    indennita: [],
  };
}

function nuovaIndennita() {
  return { tipo: '', importo: '', unita: 'mese' };
}

function ccnlVuoto() {
  return {
    id: '', nome: '', settoriCorrelati: '',
    vigenzaDa: oggi(), vigenzaA: '',
    livelli: [nuovoLivello()],
    scattiOgniAnni: '2', scattiImporto: '', scattiMassimo: '5',
    straordinarioDiurno: '', straordinarioNotturno: '', festivo: '',
    straordinarioFestivo: '', straordinarioNotturnoFestivo: '',
    tfrPercentualeAnnua: '7.41',
    ferieGiorniAnnui: '', permessiOreAnnue: '',
    metadataFonte: '', metadataUltimoAggiornamento: oggi(),
  };
}

// Converte il documento restituito da GET /sindacato/ccnl/{id} nello stato del form.
function daDocumentoAForm(doc) {
  return {
    id: doc.id,
    nome: doc.nome,
    settoriCorrelati: (doc.settori_correlati || []).join(', '),
    vigenzaDa: oggi(), // una nuova versione parte sempre da oggi: l'operatore puo' cambiarla
    vigenzaA: '',
    livelli: (doc.livelli || []).map((l) => ({
      livello: l.livello,
      pagaBaseMensile: String(l.paga_base_mensile ?? ''),
      scattoAnzianitaImporto: l.scatto_anzianita_importo != null ? String(l.scatto_anzianita_importo) : '',
      mansioni: (l.mansioni || []).join(', '),
      indennita: (l.indennita || []).map((i) => ({
        tipo: i.tipo, importo: String(i.importo), unita: i.unita,
      })),
    })),
    scattiOgniAnni: String(doc.scatti_anzianita?.ogni_anni ?? ''),
    scattiImporto: String(doc.scatti_anzianita?.importo ?? ''),
    scattiMassimo: String(doc.scatti_anzianita?.massimo_scatti ?? ''),
    straordinarioDiurno: String((doc.maggiorazioni?.straordinario_diurno ?? 0) * 100),
    straordinarioNotturno: String((doc.maggiorazioni?.straordinario_notturno ?? 0) * 100),
    festivo: String((doc.maggiorazioni?.festivo ?? 0) * 100),
    straordinarioFestivo: doc.maggiorazioni?.straordinario_festivo != null ? String(doc.maggiorazioni.straordinario_festivo * 100) : '',
    straordinarioNotturnoFestivo: doc.maggiorazioni?.straordinario_notturno_festivo != null ? String(doc.maggiorazioni.straordinario_notturno_festivo * 100) : '',
    tfrPercentualeAnnua: String((doc.tfr?.percentuale_annua ?? 0) * 100),
    ferieGiorniAnnui: String(doc.ferie_permessi?.ferie_giorni_annui ?? ''),
    permessiOreAnnue: String(doc.ferie_permessi?.permessi_ore_annue ?? ''),
    metadataFonte: '', metadataUltimoAggiornamento: oggi(),
  };
}

function formAPayload(form, operatoreEmail) {
  return {
    id: form.id.trim(),
    nome: form.nome.trim(),
    settori_correlati: form.settoriCorrelati.split(',').map((s) => s.trim()).filter(Boolean),
    vigenza: { da: form.vigenzaDa, a: form.vigenzaA || null },
    livelli: form.livelli.map((l) => ({
      livello: l.livello.trim(),
      paga_base_mensile: parseFloat(l.pagaBaseMensile) || 0,
      scatto_anzianita_importo: l.scattoAnzianitaImporto ? parseFloat(l.scattoAnzianitaImporto) : undefined,
      mansioni: l.mansioni.split(',').map((s) => s.trim()).filter(Boolean),
      indennita: l.indennita.map((i) => ({
        tipo: i.tipo.trim(), importo: parseFloat(i.importo) || 0, unita: i.unita,
      })),
    })),
    scatti_anzianita: {
      ogni_anni: parseInt(form.scattiOgniAnni, 10) || 0,
      importo: parseFloat(form.scattiImporto) || 0,
      massimo_scatti: parseInt(form.scattiMassimo, 10) || 0,
    },
    maggiorazioni: {
      straordinario_diurno: (parseFloat(form.straordinarioDiurno) || 0) / 100,
      straordinario_notturno: (parseFloat(form.straordinarioNotturno) || 0) / 100,
      festivo: (parseFloat(form.festivo) || 0) / 100,
      straordinario_festivo: form.straordinarioFestivo ? parseFloat(form.straordinarioFestivo) / 100 : undefined,
      straordinario_notturno_festivo: form.straordinarioNotturnoFestivo ? parseFloat(form.straordinarioNotturnoFestivo) / 100 : undefined,
    },
    tfr: { percentuale_annua: (parseFloat(form.tfrPercentualeAnnua) || 0) / 100 },
    ferie_permessi: {
      ferie_giorni_annui: parseFloat(form.ferieGiorniAnnui) || 0,
      permessi_ore_annue: parseFloat(form.permessiOreAnnue) || 0,
    },
    metadata: {
      fonte: form.metadataFonte.trim() || 'Inserito manualmente dal pannello operatore',
      ultimo_aggiornamento: form.metadataUltimoAggiornamento,
      aggiornato_da: operatoreEmail || 'operatore',
    },
  };
}

export default function OperatoreCcnlPage() {
  const { user } = useAuth();
  const [ccnlList, setCcnlList] = useState([]);
  const [ccnlSelezionato, setCcnlSelezionato] = useState('__nuovo__');
  const [form, setForm] = useState(ccnlVuoto());
  const [caricandoDettaglio, setCaricandoDettaglio] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const caricaLista = useCallback(async () => {
    try {
      setCcnlList(await getCcnlDisponibili());
    } catch (err) {
      toast.error('Errore nel caricamento dei CCNL');
    }
  }, []);

  useEffect(() => { caricaLista(); }, [caricaLista]);

  const handleSelezionaCcnl = async (valore) => {
    setCcnlSelezionato(valore);
    if (valore === '__nuovo__') {
      setForm(ccnlVuoto());
      return;
    }
    setCaricandoDettaglio(true);
    try {
      const doc = await getCcnlDettaglio(valore);
      setForm(daDocumentoAForm(doc));
    } catch (err) {
      toast.error('Errore nel caricamento del CCNL selezionato');
    } finally {
      setCaricandoDettaglio(false);
    }
  };

  const aggiornaCampo = (campo, valore) => setForm((prev) => ({ ...prev, [campo]: valore }));

  const aggiornaLivello = (indice, campo, valore) => {
    setForm((prev) => {
      const livelli = [...prev.livelli];
      livelli[indice] = { ...livelli[indice], [campo]: valore };
      return { ...prev, livelli };
    });
  };

  const aggiungiLivello = () => setForm((prev) => ({ ...prev, livelli: [...prev.livelli, nuovoLivello()] }));
  const rimuoviLivello = (indice) => setForm((prev) => ({
    ...prev, livelli: prev.livelli.filter((_, i) => i !== indice),
  }));

  const aggiungiIndennita = (indiceLivello) => {
    setForm((prev) => {
      const livelli = [...prev.livelli];
      livelli[indiceLivello] = {
        ...livelli[indiceLivello],
        indennita: [...livelli[indiceLivello].indennita, nuovaIndennita()],
      };
      return { ...prev, livelli };
    });
  };

  const aggiornaIndennita = (indiceLivello, indiceIndennita, campo, valore) => {
    setForm((prev) => {
      const livelli = [...prev.livelli];
      const indennita = [...livelli[indiceLivello].indennita];
      indennita[indiceIndennita] = { ...indennita[indiceIndennita], [campo]: valore };
      livelli[indiceLivello] = { ...livelli[indiceLivello], indennita };
      return { ...prev, livelli };
    });
  };

  const rimuoviIndennita = (indiceLivello, indiceIndennita) => {
    setForm((prev) => {
      const livelli = [...prev.livelli];
      livelli[indiceLivello] = {
        ...livelli[indiceLivello],
        indennita: livelli[indiceLivello].indennita.filter((_, i) => i !== indiceIndennita),
      };
      return { ...prev, livelli };
    });
  };

  const handleSalva = async () => {
    if (!form.id.trim() || !form.nome.trim()) {
      toast.error('Id e nome del CCNL sono obbligatori');
      return;
    }
    setSalvando(true);
    try {
      await creaVersioneCcnl(formAPayload(form, user?.email));
      toast.success('Nuova versione CCNL creata (la precedente resta storicizzata)');
      await caricaLista();
      const doc = await getCcnlDettaglio(form.id.trim());
      setForm(daDocumentoAForm(doc));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella creazione della nuova versione');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="operatore-ccnl-page">
        <div className="flex items-center justify-between">
          <Link to="/operatore" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-forest">
            <ArrowLeft className="w-4 h-4" /> Torna alla coda
          </Link>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileEdit className="w-6 h-6 text-forest" /> Editor CCNL
          </h1>
        </div>

        <Card className="swiss-card border-forest/20 bg-mint/20">
          <CardContent className="p-4 text-sm text-muted-foreground">
            Ogni salvataggio crea una <strong>nuova versione</strong> del CCNL: la versione precedente resta
            storicizzata e continua a essere usata per i cedolini con data di competenza passata.
          </CardContent>
        </Card>

        <Card className="swiss-card">
          <CardHeader>
            <CardTitle className="text-lg">CCNL da modificare</CardTitle>
            <CardDescription>Seleziona un CCNL esistente per crearne una nuova versione, oppure creane uno nuovo</CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={ccnlSelezionato} onValueChange={handleSelezionaCcnl}>
              <SelectTrigger className="swiss-input" data-testid="select-ccnl-editor">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__nuovo__">+ Nuovo CCNL</SelectItem>
                {ccnlList.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
            {caricandoDettaglio && <p className="text-sm text-muted-foreground mt-2">Caricamento versione corrente...</p>}
          </CardContent>
        </Card>

        <Card className="swiss-card">
          <CardHeader><CardTitle className="text-lg">Dati generali</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-uppercase">Id CCNL (slug)</Label>
                <Input
                  className="swiss-input" value={form.id}
                  disabled={ccnlSelezionato !== '__nuovo__'}
                  onChange={(e) => aggiornaCampo('id', e.target.value)}
                  placeholder="commercio-confcommercio" data-testid="input-ccnl-id"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase">Nome</Label>
                <Input className="swiss-input" value={form.nome} onChange={(e) => aggiornaCampo('nome', e.target.value)} data-testid="input-ccnl-nome" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-uppercase">Settori correlati (separati da virgola)</Label>
              <Input className="swiss-input" value={form.settoriCorrelati} onChange={(e) => aggiornaCampo('settoriCorrelati', e.target.value)} placeholder="logistica, trasporto, corrieri" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Vigenza da</Label>
                <Input type="date" className="swiss-input" value={form.vigenzaDa} onChange={(e) => aggiornaCampo('vigenzaDa', e.target.value)} data-testid="input-vigenza-da" />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Vigenza a (vuoto = aperta)</Label>
                <Input type="date" className="swiss-input" value={form.vigenzaA} onChange={(e) => aggiornaCampo('vigenzaA', e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="swiss-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Livelli, minimi e indennità</CardTitle>
                <CardDescription>Un livello per ogni categoria di inquadramento del CCNL</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={aggiungiLivello} data-testid="aggiungi-livello-btn">
                <Plus className="w-4 h-4 mr-2" /> Aggiungi livello
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {form.livelli.map((livello, indiceLivello) => (
              <Card key={indiceLivello} className="border-slate-200">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1">
                      <div className="space-y-1">
                        <Label className="label-uppercase text-xs">Livello</Label>
                        <Input className="swiss-input" value={livello.livello}
                          onChange={(e) => aggiornaLivello(indiceLivello, 'livello', e.target.value)}
                          data-testid={`livello-codice-${indiceLivello}`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="label-uppercase text-xs">Paga base mensile</Label>
                        <Input type="number" step="0.01" className="swiss-input" value={livello.pagaBaseMensile}
                          onChange={(e) => aggiornaLivello(indiceLivello, 'pagaBaseMensile', e.target.value)}
                          data-testid={`livello-paga-base-${indiceLivello}`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="label-uppercase text-xs">Scatto anzianità (override)</Label>
                        <Input type="number" step="0.01" className="swiss-input" value={livello.scattoAnzianitaImporto}
                          onChange={(e) => aggiornaLivello(indiceLivello, 'scattoAnzianitaImporto', e.target.value)} />
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-red-600 mt-5"
                      onClick={() => rimuoviLivello(indiceLivello)} data-testid={`rimuovi-livello-${indiceLivello}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-1">
                    <Label className="label-uppercase text-xs">Mansioni (separate da virgola)</Label>
                    <Input className="swiss-input" value={livello.mansioni}
                      onChange={(e) => aggiornaLivello(indiceLivello, 'mansioni', e.target.value)} />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="label-uppercase text-xs">Indennità</Label>
                      <Button variant="ghost" size="sm" onClick={() => aggiungiIndennita(indiceLivello)} data-testid={`aggiungi-indennita-${indiceLivello}`}>
                        <Plus className="w-3 h-3 mr-1" /> Aggiungi
                      </Button>
                    </div>
                    {livello.indennita.map((ind, indiceIndennita) => (
                      <div key={indiceIndennita} className="grid grid-cols-4 gap-2 items-end">
                        <Input className="swiss-input" placeholder="tipo (es. trasferta)" value={ind.tipo}
                          onChange={(e) => aggiornaIndennita(indiceLivello, indiceIndennita, 'tipo', e.target.value)} />
                        <Input type="number" step="0.01" className="swiss-input" placeholder="importo" value={ind.importo}
                          onChange={(e) => aggiornaIndennita(indiceLivello, indiceIndennita, 'importo', e.target.value)} />
                        <Select value={ind.unita} onValueChange={(v) => aggiornaIndennita(indiceLivello, indiceIndennita, 'unita', v)}>
                          <SelectTrigger className="swiss-input"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mese">al mese</SelectItem>
                            <SelectItem value="giorno">al giorno</SelectItem>
                            <SelectItem value="ora">all'ora</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-red-600"
                          onClick={() => rimuoviIndennita(indiceLivello, indiceIndennita)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>

        <Card className="swiss-card">
          <CardHeader><CardTitle className="text-lg">Scatti di anzianità</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="label-uppercase text-xs">Ogni quanti anni</Label>
              <Input type="number" className="swiss-input" value={form.scattiOgniAnni} onChange={(e) => aggiornaCampo('scattiOgniAnni', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase text-xs">Importo di default</Label>
              <Input type="number" step="0.01" className="swiss-input" value={form.scattiImporto} onChange={(e) => aggiornaCampo('scattiImporto', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase text-xs">Numero massimo scatti</Label>
              <Input type="number" className="swiss-input" value={form.scattiMassimo} onChange={(e) => aggiornaCampo('scattiMassimo', e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <Card className="swiss-card">
          <CardHeader><CardTitle className="text-lg">Maggiorazioni (%)</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="label-uppercase text-xs">Straordinario diurno</Label>
              <Input type="number" step="0.1" className="swiss-input" value={form.straordinarioDiurno} onChange={(e) => aggiornaCampo('straordinarioDiurno', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase text-xs">Straordinario notturno</Label>
              <Input type="number" step="0.1" className="swiss-input" value={form.straordinarioNotturno} onChange={(e) => aggiornaCampo('straordinarioNotturno', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase text-xs">Festivo</Label>
              <Input type="number" step="0.1" className="swiss-input" value={form.festivo} onChange={(e) => aggiornaCampo('festivo', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase text-xs">Straordinario festivo</Label>
              <Input type="number" step="0.1" className="swiss-input" value={form.straordinarioFestivo} onChange={(e) => aggiornaCampo('straordinarioFestivo', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase text-xs">Straord. notturno festivo</Label>
              <Input type="number" step="0.1" className="swiss-input" value={form.straordinarioNotturnoFestivo} onChange={(e) => aggiornaCampo('straordinarioNotturnoFestivo', e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <Card className="swiss-card">
          <CardHeader><CardTitle className="text-lg">TFR, ferie e permessi</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="label-uppercase text-xs">TFR annuo (%)</Label>
              <Input type="number" step="0.01" className="swiss-input" value={form.tfrPercentualeAnnua} onChange={(e) => aggiornaCampo('tfrPercentualeAnnua', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase text-xs">Ferie (giorni annui)</Label>
              <Input type="number" className="swiss-input" value={form.ferieGiorniAnnui} onChange={(e) => aggiornaCampo('ferieGiorniAnnui', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase text-xs">Permessi (ore annue)</Label>
              <Input type="number" className="swiss-input" value={form.permessiOreAnnue} onChange={(e) => aggiornaCampo('permessiOreAnnue', e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <Card className="swiss-card">
          <CardHeader><CardTitle className="text-lg">Fonte</CardTitle></CardHeader>
          <CardContent>
            <Label className="label-uppercase text-xs">Riferimento normativo di questa versione</Label>
            <Input className="swiss-input mt-1" value={form.metadataFonte}
              onChange={(e) => aggiornaCampo('metadataFonte', e.target.value)}
              placeholder="es. Rinnovo CCNL 2027, art. 12" />
          </CardContent>
        </Card>

        <Separator />

        <div className="flex justify-end">
          <Button className="swiss-btn-primary h-12 px-8" onClick={handleSalva} disabled={salvando} data-testid="salva-ccnl-btn">
            <Save className="w-4 h-4 mr-2" /> {salvando ? 'Salvataggio...' : 'Crea nuova versione'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
